/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */

import i18n from "i18next";

const MINUTES_IN_DAY = 1440;

const currencyOptions = {
  values: ["EUR", "USD"],
  names: [
    { uid: "EUR", name: "EUR" },
    { uid: "USD", name: "USD" },
  ],
};

const getWeekDaysOptions = () => [
  { uid: 1, name: i18n.t("weekDayMonday") },
  { uid: 2, name: i18n.t("weekDayTuesday") },
  { uid: 3, name: i18n.t("weekDayWednesday") },
  { uid: 4, name: i18n.t("weekDayThursday") },
  { uid: 5, name: i18n.t("weekDayFriday") },
  { uid: 6, name: i18n.t("weekDaySaturday") },
  { uid: 0, name: i18n.t("weekDaySunday") },
];

const statusValues = [
  { label: "pending", status: "pending", color: "#f39c12" },
  { label: "processing", status: "processing", color: "#3498db" },
  { label: "confirmed", status: "confirmed", color: "#2ecc71" },
  { label: "canceled", status: "canceled", color: "#e74c3c" },
];

const isPositiveInteger = (value) => /^[0-9]\d*$/.test(String(value));

const getMinutesFromHHMM = (value) => {
  if (!value || typeof value !== "string" || !value.includes(":")) return null;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

const getAllowedHoursRange = (resource = {}) => {
  const [startHour, startMinutes] = (resource?.startTime || "00:00").split(":").map(Number);
  const [endHour, endMinutes] = (resource?.endTime || "23:59").split(":").map(Number);

  return { startHour, startMinutes, endHour, endMinutes };
};

const isStartWithinAllowedHours = (startDate, resource = {}) => {
  const { startHour, startMinutes, endHour, endMinutes } = getAllowedHoursRange(resource);

  const startValid =
    startDate.getHours() > startHour ||
    (startDate.getHours() === startHour && startDate.getMinutes() >= startMinutes);

  const startInsideEnd =
    startDate.getHours() < endHour ||
    (startDate.getHours() === endHour && startDate.getMinutes() <= endMinutes);

  return startValid && startInsideEnd;
};

const isEventInDateRangeByResource = ({ start, end, durationMinutes, resource }) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const diff = (endDate.getTime() - startDate.getTime()) / 60000;

  if (durationMinutes && diff !== durationMinutes) return "invalid_duration";

  if (!isStartWithinAllowedHours(startDate, resource)) return "outside_allowed_hours";

  return null;
};

const formatTimeToFormatedString = (totalMinutes) => {
  if (typeof totalMinutes !== "number") return "0 min";

  const days = Math.floor(totalMinutes / MINUTES_IN_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_IN_DAY) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];

  if (days > 0) parts.push(`${days} ${days === 1 ? "día" : "días"}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hora" : "horas"}`);
  if (minutes > 0) parts.push(`${minutes} min`);

  return parts.length > 0 ? parts.join(" ") : "0 min";
};

const getStatusColor = (value) => {
  const color = statusValues.find((element) => element?.status === value)?.color || "#7f8c8d";

  return color;
};

const addColorByStatusToEvents = (events) => {
  const coloredEvents = (events || []).map((event) => {
    const status = event?.extendedProps?.status || event?.status || "pending";
    const color = getStatusColor(status);

    return {
      ...event,
      backgroundColor: color,
      borderColor: color,
      textColor: "#fff",
    };
  });

  return coloredEvents;
};

const getApartments = (projects, projectId) => {
  if (projects) {
    if (Object.hasOwnProperty.call(projects, projectId)) {
      if (Object.hasOwnProperty.call(projects[projectId], "apartments")) {
        return Object.keys(projects[projectId].apartments);
      }
      return [];
    }
    return [];
  }
  return [];
};

const getApartmentsNames = (projects, projectId, apartments) => {
  if (projects) {
    if (Object.hasOwnProperty.call(projects, projectId)) {
      if (Object.hasOwnProperty.call(projects[projectId], "apartments")) {
        const apartmentIds = Object.keys(projects[projectId].apartments);

        return Object.values(apartments || {})
          .filter(({ apartmentId }) => apartmentIds.indexOf(apartmentId) >= 0)
          .map(({ apartmentId, name }) => ({
            uid: apartmentId,
            name,
          }));
      }

      return [];
    }

    return [];
  }

  return [];
};

const getClientsNames = (clients, availableClientIds = []) => {
  if (!clients || !Array.isArray(availableClientIds)) return [];

  return Object.values(clients || {})
    .filter(({ uid }) => availableClientIds.indexOf(uid) >= 0)
    .map(({ uid, firstName, lastName }) => ({
      uid,
      name: `${firstName} ${lastName}`,
    }));
};

const formatTimestampToProjectTimeZone = (value, timeZone = "UTC") => {
  const timestamp = Number(value);

  if (!Number.isFinite(timestamp)) return "-";

  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
};

const getCalendarInitialDate = (initialDate) => {
  const focusTimestamp = Number(initialDate);

  if (Number.isFinite(focusTimestamp)) return focusTimestamp;

  const parsedFocusDate = new Date(initialDate).getTime();

  if (Number.isFinite(parsedFocusDate)) return parsedFocusDate;

  return Date.now();
};

const getClientReservationsList = (clientReservations = {}) =>
  Object.values(clientReservations || {}).flatMap((reservationsByClient) =>
    Object.values(reservationsByClient || {}).filter((reservation) => !!reservation?.uid)
  );

const getClientReservationsById = (clientReservations = []) => {
  const reservationById = {};

  (clientReservations || []).forEach((reservation) => {
    reservationById[reservation.uid] = reservation;
  });

  return reservationById;
};

const getActiveClientReservations = (clientReservations = [], now = Date.now()) =>
  (clientReservations || []).filter((reservation) => {
    const reservationEndDate = Number(reservation?.endDate);

    return Number.isFinite(reservationEndDate) && reservationEndDate >= now;
  });

const getClientReservationsDerived = (clientReservations = {}, now = Date.now()) => {
  const allClientReservations = getClientReservationsList(clientReservations);

  return {
    clientReservationsById: getClientReservationsById(allClientReservations),
    activeClientReservations: getActiveClientReservations(allClientReservations, now),
  };
};

const getEventsList = (events) => {
  if (!events || typeof events !== "object") return [];

  return Object.values(events);
};

const getEventsByResourceId = (eventsByResource = {}, resourceId = "") => {
  if (!resourceId) return [];

  const resourceEvents = eventsByResource?.[resourceId];
  if (!resourceEvents || typeof resourceEvents !== "object") return [];

  return Object.values(resourceEvents)
    .flatMap((eventsByClientReservation) => getEventsList(eventsByClientReservation))
    .filter(Boolean);
};

const formatEventRowWithClientReservation = (
  eventRow = {},
  clientReservationsById = {},
  projects = {}
) => {
  const reservation = clientReservationsById?.[eventRow?.clientReservationId] || {};
  const resolvedApartmentId = eventRow?.apartmentId || reservation?.apartmentId || "";
  const resolvedProjectId = eventRow?.projectId || reservation?.projectId || "";
  const projectInfo = projects?.[resolvedProjectId] || {};

  return {
    ...eventRow,
    apartmentId: resolvedApartmentId,
    apartmentName: eventRow?.apartmentName || reservation?.apartmentName || "",
    projectId: resolvedProjectId,
    projectName: eventRow?.projectName || reservation?.projectName || projectInfo?.name || "",
  };
};

const formatAmenityEventsForTable = (
  amenityEvents = {},
  amenities = {},
  apartments = {},
  projects = {},
  clientReservationsById = {}
) => {
  if (!amenityEvents || !amenities) return [];

  const allEvents = [];

  Object.entries(amenityEvents).forEach(([amenityId, eventsByClientReservation]) => {
    const amenity = amenities[amenityId] || {};
    const amenityName = amenity?.name || "";

    Object.values(eventsByClientReservation || {}).forEach((eventsByReservation) => {
      const eventsList = getEventsList(eventsByReservation);

      eventsList.forEach((amenityEvent) => {
        const aptInfo = apartments[amenityEvent?.apartmentId] || {};
        const apartmentName = aptInfo?.name || "";
        const projectId = aptInfo?.projectId || "";
        const projectInfo = projects[projectId] || {};
        const projectName = projectInfo?.name || "";
        const status = amenityEvent?.status || amenityEvent?.extendedProps?.status || "pending";

        const row = {
          ...amenityEvent,
          amenityId,
          amenityName,
          projectId,
          projectName,
          apartmentName,
          status,
          associatedServicesCount: amenityEvent?.associatedServices?.length || 0,
        };

        allEvents.push(formatEventRowWithClientReservation(row, clientReservationsById, projects));
      });
    });
  });

  return allEvents;
};

const formatServiceEventsForTable = (
  serviceEvents = {},
  services = {},
  apartments = {},
  projects = {},
  clientReservationsById = {}
) => {
  if (!serviceEvents || !services) return [];

  const allEvents = [];

  Object.entries(serviceEvents).forEach(([serviceId, events]) => {
    const serviceInfo = services[serviceId] || {};
    const serviceName = serviceInfo?.name || "";

    Object.entries(events || {}).forEach(([clientReservationId, eventsByReservation]) => {
      const eventsList = getEventsList(eventsByReservation);

      eventsList.forEach((event) => {
        const apartmentId = event?.apartmentId || event?.extendedProps?.apartmentId || "";
        const aptInfo = apartments[apartmentId] || {};
        const eventProjectId = event?.projectId || event?.extendedProps?.projectId || "";
        const projectId = aptInfo?.projectId || eventProjectId;
        const projectInfo = projects[projectId] || {};
        const status = event?.status || event?.extendedProps?.status || "pending";

        const row = {
          ...event,
          apartmentId,
          clientReservationId:
            event?.clientReservationId ||
            event?.extendedProps?.clientReservationId ||
            clientReservationId,
          serviceId,
          serviceName,
          projectId: projectId || "",
          projectName: projectInfo?.name || "",
          apartmentName: aptInfo?.name || "",
          status,
        };

        allEvents.push(formatEventRowWithClientReservation(row, clientReservationsById, projects));
      });
    });
  });

  return allEvents.sort((a, b) => Number(a.start) - Number(b.start));
};

const getActiveClientReservationIds = (clientReservations = [], now = Date.now()) => {
  const activeClientReservationIds = (clientReservations || [])
    .filter((clientReservation) => {
      const clientReservationEndDate = Number(clientReservation?.endDate);

      const isClientReservationActive =
        Number.isFinite(clientReservationEndDate) && clientReservationEndDate > now;

      return isClientReservationActive;
    })
    .map((activeClientReservation) => activeClientReservation?.uid)
    .filter(Boolean);

  return new Set(activeClientReservationIds);
};

const renderEventContent = (arg) => {
  const extendedProps = arg.event?.extendedProps || arg.event?._def?.extendedProps || {};
  const status = extendedProps?.status || "pending";
  const color = getStatusColor(status);

  const parsedServiceQuantity = Number(extendedProps?.serviceRequestNumber);
  const parsedResourceQuantity = Number(extendedProps?.amenityRequestNumber);

  const serviceQuantity = Number.isFinite(parsedServiceQuantity) ? parsedServiceQuantity : null;
  const resourceQuantity = Number.isFinite(parsedResourceQuantity) ? parsedResourceQuantity : 1;

  const associatedServiceEventIds = extendedProps?.associatedServiceEventIds;
  const associatedServices = extendedProps?.associatedServices;

  const associatedServiceCountFromIds =
    associatedServiceEventIds && typeof associatedServiceEventIds === "object"
      ? Object.keys(associatedServiceEventIds).length
      : 0;

  const associatedServiceCountFromList = Array.isArray(associatedServices)
    ? associatedServices.length
    : 0;

  const associatedServiceCount = Math.max(
    associatedServiceCountFromIds,
    associatedServiceCountFromList
  );

  const title = arg.event?.title;

  const hasAssociatedServices = associatedServiceCount > 0;

  const serviceSingular = String(i18n.t("service")).toLowerCase();
  const servicePlural = String(i18n.t("services")).toLowerCase();
  const resourceSingular = String(i18n.t("resource")).toLowerCase();
  const resourcePlural = String(i18n.t("resources")).toLowerCase();

  const isAmenityEvent = !!extendedProps?.amenityId;

  const mainQuantity = isAmenityEvent
    ? resourceQuantity
    : serviceQuantity ?? (associatedServiceCount > 0 ? associatedServiceCount : 1);

  const mainSingularLabel = isAmenityEvent ? resourceSingular : serviceSingular;
  const mainPluralLabel = isAmenityEvent ? resourcePlural : servicePlural;

  const associatedServicesLine =
    isAmenityEvent && hasAssociatedServices
      ? `<div style="font-size: 14px; font-weight: bold;">${associatedServiceCount} ${
          associatedServiceCount === 1 ? serviceSingular : servicePlural
        }</div>`
      : "";

  return {
    domNodes: [
      Object.assign(document.createElement("div"), {
        style: `
          width: 100%;
          height: 100%;
          background: ${color};
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: start;
          align-items: center;
          padding-top: 2px;
        `,
        innerHTML: `
          <div style="font-size: 14px; font-weight: bold;">
            ${arg.timeText}
          </div>
          <div style="font-size: 14px; font-weight: bold;">
            ${title}
          </div>
          <div style="font-size: 14px; font-weight: bold;">
          ${mainQuantity} ${mainQuantity === 1 ? mainSingularLabel : mainPluralLabel}
          </div>
          ${associatedServicesLine}

        `,
      }),
    ],
  };
};

const getAllowedMaxHourInCalendarView = (durationTime) => 24 + Math.ceil(durationTime / 60);

const overlaps = (startA, endA, startB, endB) => !(startA >= endB || endA <= startB);

const validateEventLimit = (newEvent, existingEvents = [], resource, quantityField) => {
  if (!newEvent || !resource) return false;

  const globalLimit = resource?.resourcesLimited
    ? parseInt(resource?.resourcesLimitNumber, 10)
    : Infinity;
  const clientLimit = resource?.clientLimit ? parseInt(resource?.clientLimit, 10) : Infinity;

  const newStart = new Date(newEvent?.start);
  const newEnd = new Date(newEvent?.end);

  if (Number.isNaN(newStart.valueOf()) || Number.isNaN(newEnd.valueOf())) return false;

  let usedGlobal = Number(newEvent?.[quantityField] || 1);
  let usedByClient = Number(newEvent?.[quantityField] || 1);

  (existingEvents || []).forEach((event) => {
    if (!event || event?.id === newEvent?.id) return;

    const exStart = new Date(event?.start);
    const exEnd = new Date(event?.end);

    if (Number.isNaN(exStart.valueOf()) || Number.isNaN(exEnd.valueOf())) return;
    if (!overlaps(newStart, newEnd, exStart, exEnd)) return;

    const qty = Number(event?.[quantityField] || 1);
    usedGlobal += qty;

    if (newEvent?.apartmentId && event?.apartmentId === newEvent?.apartmentId) {
      usedByClient += qty;
    }
  });

  return usedGlobal > globalLimit || usedByClient > clientLimit;
};

const validateServiceEventLimit = (newEvent, existingEvents, service) =>
  validateEventLimit(newEvent, existingEvents, service, "serviceRequestNumber");

const validateAmenityEventLimit = (newEvent, existingEvents, amenity) =>
  validateEventLimit(newEvent, existingEvents, amenity, "amenityRequestNumber");

const amenityUtils = {
  currencyOptions,
  getWeekDaysOptions,
  statusValues,
  isPositiveInteger,
  getMinutesFromHHMM,
  getAllowedHoursRange,
  isEventInDateRangeByResource,
  formatTimeToFormatedString,
  getApartmentsNames,
  getApartments,
  getActiveClientReservationIds,
  getClientReservationsList,
  getClientReservationsById,
  getActiveClientReservations,
  getClientReservationsDerived,
  getEventsByResourceId,
  getCalendarInitialDate,
  getClientsNames,
  formatTimestampToProjectTimeZone,
  formatAmenityEventsForTable,
  formatServiceEventsForTable,
  addColorByStatusToEvents,
  getAllowedMaxHourInCalendarView,
  getStatusColor,
  renderEventContent,
  validateServiceEventLimit,
  validateAmenityEventLimit,
};

export default amenityUtils;
